import React, { useState, useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { format } from "date-fns";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { isSuperUser } from "@/types/user";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";
import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";
import { NannyInvite } from "@/components/submission/types";

interface EmployeeInvitesProps {
  employeeId: string;
}

// Define interface for API request config
interface ApiRequestConfig {
  urlParams?: Record<string, any>;
  queryParams?: Record<string, any>;
  method?: string;
  body?: any;
}

const formSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
});

const EmployeeInvites: React.FC<EmployeeInvitesProps> = ({ employeeId }) => {
  const [invites, setInvites] = useState<NannyInvite[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  
  // Get auth and URL params for superuser functionality
  const { user } = useAuth();
  const isSuperUserAccount = isSuperUser(user);
  const [searchParams] = useSearchParams();
  const [selectedUserId, setSelectedUserId] = useState<string>("current");
  
  // Extract userId from URL if superuser
  useEffect(() => {
    const userIdParam = searchParams.get('userId');
    if (isSuperUserAccount && userIdParam) {
      setSelectedUserId(userIdParam);
    }
  }, [searchParams, isSuperUserAccount]);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
    },
  });

  useEffect(() => {
    if (employeeId) {
      fetchInvites();
    }
  }, [employeeId, selectedUserId]);

  const fetchInvites = async () => {
    try {
      const requestConfig: ApiRequestConfig = {
        urlParams: { employeeId },
      };
      
      // Add userId to queryParams if superuser and selectedUserId is not "current"
      if (isSuperUserAccount && selectedUserId !== "current") {
        requestConfig.queryParams = { userId: selectedUserId };
      }
      
      const response = await ApiService.request<{ invites: NannyInvite[] }>(
        EndpointKeys.GET_NANNY_INVITES,
        requestConfig
      );
      setInvites(response.invites);
    } catch (error) {
      console.error("Error fetching invites:", error);
      toast({
        title: "Error",
        description: "Failed to fetch invites",
        variant: "destructive",
      });
    }
  };

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      setIsLoading(true);
      
      const requestConfig: ApiRequestConfig = {
        method: "POST",
        urlParams: { employeeId },
        body: values,
      };
      
      // Add userId to queryParams if superuser and selectedUserId is not "current"
      if (isSuperUserAccount && selectedUserId !== "current") {
        requestConfig.queryParams = { userId: selectedUserId };
      }
      
      await ApiService.request(EndpointKeys.SEND_NANNY_INVITE, requestConfig);

      toast({
        title: "Success",
        description: "Invite sent successfully",
      });

      form.reset();
      await fetchInvites();
    } catch (error) {
      console.error("Error sending invite:", error);
      toast({
        title: "Error",
        description: "Failed to send invite",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadgeVariant = (status: NannyInvite["status"]) => {
    switch (status) {
      case "PENDING":
        return "secondary";
      case "ACCEPTED":
        return "default";
      case "EXPIRED":
        return "destructive";
      default:
        return "outline";
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Send New Invite</CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email Address</FormLabel>
                    <FormControl>
                      <div className="flex space-x-2">
                        <Input placeholder="employee@example.com" {...field} />
                        <Button type="submit" disabled={isLoading}>
                          {isLoading ? "Sending..." : "Send Invite"}
                        </Button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </form>
          </Form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Invite History</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Sent Date</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead>Accepted Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invites.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="text-center text-muted-foreground"
                  >
                    No invites sent yet
                  </TableCell>
                </TableRow>
              ) : (
                invites.map((invite) => (
                  <TableRow key={invite.id}>
                    <TableCell>{invite.email}</TableCell>
                    <TableCell>
                      <Badge variant={getStatusBadgeVariant(invite.status)}>
                        {invite.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {format(new Date(invite.created_at), "PPp")}
                    </TableCell>
                    <TableCell>
                      {format(new Date(invite.expires_at), "PPp")}
                    </TableCell>
                    <TableCell>
                      {invite.accepted_at
                        ? format(new Date(invite.accepted_at), "PPp")
                        : "-"}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmployeeInvites;
